import React, { useEffect, useState } from 'react';
import { Chip } from '../ModuleFrame';
import { Sparkline } from '../charts';
import { apiPost } from '../../utils/backendApi';
import { ACTIVE, ActiveBar, Composer, EmptyDetail, RunCard, Shot, UsageLine, ago } from './shared';

// Performance phase: composer (target, pages/questions) + run list + trend |
// budgets row, per-page table with budget chips, agent latency, filmstrip.
const kb = (b) => (b == null ? '—' : `${Math.round(b / 1024)} KB`);
const ms = (v) => (v == null ? '—' : `${v} ms`);

export default function Perf({ org, app, runs, info, sel, setSel, canEdit, busy, act, onTab }) {
  const mine = runs.filter((r) => r.kind === 'perf');
  const run = mine.find((r) => r.id === sel) || null;
  const [target, setTarget] = useState(info.targets?.[0]?.env || 'uat');
  useEffect(() => { if (info.targets?.length && !info.targets.some((t) => t.env === target)) setTarget(info.targets[0].env); }, [info.targets, target]);
  const start = (brief) => act('start', () => apiPost(`/lab/${org.id}/runs`, { appId: app.id, kind: 'perf', brief: brief || 'Measure the app\'s pages and agent response time', target }).then((out) => setSel(out.runId)));
  const done = mine.filter((r) => r.status === 'done' && r.findings?.perf).slice().reverse().slice(-10);
  const worstLcp = (r) => Math.max(0, ...(r.findings.perf.pages || []).map((p) => p.lcpMs || 0));
  const worstAgent = (r) => Math.max(0, ...(r.findings.perf.agent || []).map((a) => a.ms || 0));
  const budgets = info.perf?.budgets || {};
  return (
    <>
      <div className="lab-list" data-nova-note={`Perf phase: ${mine.length} perf runs, ${done.length} done${run ? `; run ${run.id} (${run.status}${run.findings?.verdict ? `, ${run.findings.verdict}` : ''}) is open` : ''}, target ${target}. NoVA: test_app starts a run (the Measure button here is the perf kind); findings are read against the budgets on this screen.`}>
        <Composer placeholder={`Optional: name the paths to measure, e.g. "/items, /jobs and /pipelines" — otherwise the start page and up to ${info.perf?.maxPages ?? 6} linked pages`} buttonLabel="Measure" busyLabel="Starting…" canEdit={canEdit} busy={busy === 'start'} onStart={start} targets={info.targets || []} target={target} setTarget={setTarget} allowEmpty rows={2}
          note={<p className="cs-help" style={{ margin: 0 }}>Read-only: loads pages in a real browser and asks the agent sample questions. Uses browser minutes.</p>} />
        {done.length > 1 && (
          <div className="lab-trend">
            <div><span className="mf-muted">LCP, worst page</span><Sparkline points={done.map(worstLcp)} width={140} height={28} /></div>
            <div><span className="mf-muted">Agent answer</span><Sparkline points={done.map(worstAgent)} width={140} height={28} color="#a78bfa" /></div>
          </div>
        )}
        {!mine.length && <p className="mf-muted lab-list-empty">No performance runs yet.</p>}
        {mine.map((r) => {
          const pages = r.findings?.perf?.pages || [];
          const worst = pages.slice().sort((a, b) => (b.lcpMs || 0) - (a.lcpMs || 0))[0];
          return <RunCard key={r.id} run={r} selected={r.id === sel} onClick={() => setSel(r.id)} extra={pages.length ? <span className="mf-muted lab-card-sub">{pages.length} pages · worst {worst?.path} {ms(worst?.lcpMs)}</span> : null} />;
        })}
      </div>
      <div className="lab-detail">
        {!run && <EmptyDetail phase="perf"><p className="cs-help">Budgets: LCP {budgets.lcpMs} ms · TTFB {budgets.ttfbMs} ms · CLS {budgets.cls} · agent {budgets.agentMs} ms — change them in live config <code>lab.perf.budgets</code>.</p></EmptyDetail>}
        {run && <PerfDetail org={org} run={run} info={info} canEdit={canEdit} busy={busy} act={act} onTab={onTab} />}
      </div>
    </>
  );
}

function PerfDetail({ org, run: r, info, canEdit, busy, act, onTab }) {
  const p = r.findings?.perf || {};
  const pages = p.pages || [];
  const agent = p.agent || [];
  const b = p.budgets || {};
  const [cur, setCur] = useState(0);
  useEffect(() => { setCur(Math.max(0, pages.findIndex((x) => x.over?.length))); }, [r.id, pages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <header className="lab-detail-head">
        <div>
          <h3>Performance · {r.target?.env}</h3>
          <span className="mf-muted">{ACTIVE.has(r.status) ? r.stage : `${pages.length} pages · ${agent.length} questions · ${ago(r.finishedAt || r.updatedAt)}`} {r.findings?.verdict ? <Chip tone={r.findings.verdict === 'pass' ? 'ok' : 'danger'}>{r.findings.verdict === 'pass' ? 'within budget' : 'over budget'}</Chip> : null}</span>
        </div>
        {r.target?.url && <a className="cs-btn cs-btn-ghost cs-btn-sm" href={r.target.url} target="_blank" rel="noreferrer">Open app ↗</a>}
      </header>
      <ActiveBar org={org} run={r} canEdit={canEdit} busy={busy} act={act} etaSec={info?.eta?.[r.kind]} />
      {r.error && <p className="cs-form-error" style={{ margin: 0 }}>{r.error}</p>}
      {r.status === 'done' && (
        <>
          <div className="lab-budgets">
            <span className="mf-muted">Budgets</span>
            <Chip>LCP ≤ {b.lcpMs} ms</Chip><Chip>TTFB ≤ {b.ttfbMs} ms</Chip><Chip>CLS ≤ {b.cls}</Chip><Chip>agent ≤ {b.agentMs} ms</Chip>
            <span className="mf-muted" style={{ fontSize: 12 }}>· live config <code>lab.perf.budgets</code></span>
          </div>
          <div className="lab-table-wrap">
            <table className="lab-table">
              <thead><tr><th>Page</th><th>TTFB</th><th>DCL</th><th>LCP</th><th>CLS</th><th>Size</th><th>Req</th><th /></tr></thead>
              <tbody>
                {pages.map((pg, i) => (
                  <tr key={pg.url} className={`${i === cur ? 'is-cur' : ''} ${pg.over?.length ? 'is-over' : ''}`} onClick={() => setCur(i)}>
                    <td className="lab-td-path"><span title={pg.title}>{pg.path}</span>{pg.error ? <span className="cs-form-error lab-step-sub" style={{ padding: 0 }}>{pg.error}</span> : null}</td>
                    <td className={pg.over?.includes('ttfb') ? 'is-over' : ''}>{ms(pg.ttfbMs)}</td>
                    <td>{ms(pg.domContentLoadedMs)}</td>
                    <td className={pg.over?.includes('lcp') ? 'is-over' : ''}>{ms(pg.lcpMs)}</td>
                    <td className={pg.over?.includes('cls') ? 'is-over' : ''}>{pg.cls ?? '—'}</td>
                    <td>{kb(pg.transferBytes)}</td>
                    <td>{pg.requests ?? '—'}</td>
                    <td>{pg.over?.length ? <Chip tone="danger">{pg.over.join(', ')}</Chip> : pg.ok ? <Chip tone="ok">ok</Chip> : <Chip tone="danger">error</Chip>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {agent.length > 0 && (
            <section className="lab-section">
              <h4 className="lab-h">Agent answer time</h4>
              <ul className="mf-list">
                {agent.map((a, i) => (
                  <li key={i} className="mf-row">
                    <span className="mf-row-grow">{a.q}</span>
                    <Chip tone={a.over ? 'danger' : a.ok ? 'ok' : 'warn'}>{a.ms} ms{a.ok ? '' : ' · no answer'}</Chip>
                    <span className="mf-muted" style={{ fontSize: 12 }}>{a.cited} cited</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {pages[cur]?.screenshotKey && (
            <div className="lab-film">
              <Shot key={pages[cur].screenshotKey} org={org} runId={r.id} k={pages[cur].screenshotKey} large auto alt={pages[cur].path} />
              <div className="lab-film-nav">
                <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={cur <= 0} onClick={() => setCur(cur - 1)}>‹ prev</button>
                <span className="mf-muted">{pages[cur].url}{pages[cur].title ? ` · ${pages[cur].title}` : ''}</span>
                <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={cur >= pages.length - 1} onClick={() => setCur(cur + 1)}>next ›</button>
              </div>
            </div>
          )}
          {r.findings?.summary && <p className={r.findings.verdict === 'pass' ? 'lab-verdict is-ok' : 'lab-verdict is-fail'}>{r.findings.summary}</p>}
          {r.findings?.testRunId && <p className="mf-muted lab-foot"><button type="button" className="cs-btn-link mf-muted" onClick={() => onTab?.('ship')}>QA run (performance gate) →</button></p>}
        </>
      )}
      <UsageLine run={r} />
    </>
  );
}
